import React, { useEffect } from "react";
import { applySeoOverride } from "../hooks/useSeo";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronRight, ShieldCheck, UserCheck, FileCheck2, MapPin, Star, Users, DollarSign, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { parseLanding, serviceRate } from "../data/seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useJobModal } from "../context/JobModalContext";
import { CITIES } from "../mock";

export default function ServiceCityPage() {
  const { landingSlug } = useParams();
  const navigate = useNavigate();
  const { openJobModal } = useJobModal();
  const parsed = parseLanding(landingSlug);

  useEffect(() => {
    if (parsed) {
      document.title = `${parsed.service.label} in ${parsed.city.name} | SimplySitter`;
      const m = document.querySelector('meta[name="description"]') || (() => { const el = document.createElement("meta"); el.name = "description"; document.head.appendChild(el); return el; })();
      m.setAttribute("content", `Looking for a ${parsed.service.label.toLowerCase()} in ${parsed.city.name}, ${parsed.city.state}? Post your job free & hire a vetted, background-checked ${parsed.service.label.toLowerCase()} in ${parsed.city.name}. 1 month free.`);
    }
    applySeoOverride();
    return () => { document.title = "SimplySitter"; };
  }, [parsed]);

  if (!parsed) {
    return (
      <div><Header /><div className="hero-gradient py-24 px-5 text-center min-h-[60vh]">
        <h1 className="font-display text-4xl text-[#1f1a1b]">Page not found</h1>
        <Link to="/cities" className="inline-flex mt-6 rounded-xl bg-[#7c2233] text-white px-6 py-3">Browse cities</Link>
      </div><Footer /></div>
    );
  }

  const { service, city, seo } = parsed;
  const stats = seo?.stats || { families: "500+", caregivers: "300+", avgRate: "$24/hr", response: "1–2 days" };
  const rate = serviceRate(service.id, city.slug);
  const start = () => openJobModal({ service: service.id, location: city.name, citySlug: city.slug });
  const otherServices = ["nanny", "babysitter", "housekeeper", "senior-care"].filter((s) => s !== parsed.serviceSlug);

  return (
    <div>
      <Header />
      <section className="hero-gradient">
        <div className="max-w-7xl mx-auto px-5 pt-10 pb-14">
          <nav className="flex items-center gap-1.5 text-[13px] text-[#8a8285]">
            <Link to="/" className="hover:text-[#7c2233]">Home</Link><ChevronRight className="w-3.5 h-3.5" />
            <Link to={`/city/${city.slug}`} className="hover:text-[#7c2233]">{city.name}</Link><ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#5a5254]">{service.label}</span>
          </nav>
          <div className="mt-4 max-w-3xl">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white/70 px-3 py-1 text-[13px] text-[#5a5254]">
              <Star className="w-3.5 h-3.5 text-[#7c2233] fill-[#7c2233]" /> Trusted by families in {city.name}
            </div>
            <h1 className="font-display text-[2.4rem] leading-[1.08] sm:text-5xl text-[#1f1a1b] mt-4">
              Find a {service.label.toLowerCase() === "senior care" ? "senior caregiver" : service.label.toLowerCase()} in {city.name}, {city.state}
            </h1>
            <p className="mt-4 text-[17px] leading-relaxed text-[#5a5254]">{seo?.intro}</p>
            <p className="mt-3 text-[16px] leading-relaxed text-[#5a5254]">
              Whether you need {service.angle}, every {service.label.toLowerCase()} on SimplySitter is identity-verified, personally interviewed and background-checked.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={start} className="rounded-xl bg-[#7c2233] hover:bg-[#661b29] text-white font-medium px-6 py-3.5 transition-colors">Post a job free</button>
              <Link to={`/city/${city.slug}`} className="rounded-xl border border-[#7c2233]/30 text-[#7c2233] font-medium px-6 py-3.5 hover:bg-[#faf1f2] transition-colors">All services in {city.name}</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#fdfaf7] border-y border-black/[0.06]">
        <div className="max-w-5xl mx-auto px-5 py-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[[Users, stats.families, "families served"], [ShieldCheck, stats.caregivers, `${service.plural.toLowerCase()} nearby`], [DollarSign, rate.replace("/hr", ""), "typical rate /hr"], [Clock, stats.response, "typical response"]].map(([Icon, v, l], i) => (
            <div key={i} className="text-center">
              <Icon className="w-5 h-5 text-[#7c2233] mx-auto" strokeWidth={1.8} />
              <div className="font-display text-2xl text-[#1f1a1b] mt-1">{v}</div>
              <div className="text-[13px] text-[#8a8285]">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 py-14 grid md:grid-cols-3 gap-5">
          {[[ShieldCheck, "Verified & Trusted", "Every candidate verifies their identity before joining."], [UserCheck, "Interviewed", "We personally interview applicants before approving profiles."], [FileCheck2, "Background Checked", "Background and reference checks help you hire with confidence."]].map(([Icon, t, d]) => (
            <div key={t} className="rounded-2xl border border-black/[0.07] p-7">
              <div className="w-12 h-12 rounded-xl bg-[#f7e4e6] flex items-center justify-center"><Icon className="w-6 h-6 text-[#7c2233]" strokeWidth={1.7} /></div>
              <h3 className="font-display text-xl text-[#1f1a1b] mt-4">{t}</h3>
              <p className="text-[15px] text-[#5a5254] mt-2">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-t border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-5 py-14">
          <h2 className="font-display text-3xl sm:text-4xl text-[#1f1a1b]">Hire a trusted {service.label.toLowerCase()} in {city.name}</h2>
          <p className="text-[16px] text-[#5a5254] mt-4 leading-relaxed">
            Looking for a reliable {service.label.toLowerCase()} in {city.name}, {city.state}? SimplySitter connects {city.name} families with experienced, background-checked caregivers for {service.angle}. Post your job for free and receive applications only from vetted candidates.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 mt-8">
            <div>
              <h3 className="font-display text-lg text-[#1f1a1b]">What does a {service.label.toLowerCase()} in {city.name} cost?</h3>
              <p className="text-[15px] text-[#5a5254] mt-1">Rates for a {service.label.toLowerCase()} in {city.name} typically range {rate}. With SimplySitter there's no placement fee &mdash; just a transparent membership, 1 month free.</p>
            </div>
            <div>
              <h3 className="font-display text-lg text-[#1f1a1b]">How fast can I find a {service.label.toLowerCase()} in {city.name}?</h3>
              <p className="text-[15px] text-[#5a5254] mt-1">Most {city.name} families receive their first applications within {stats.response} of posting a {service.label.toLowerCase()} job.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#fdfaf7]">
        <div className="max-w-7xl mx-auto px-5 py-14">
          <h2 className="font-display text-3xl text-[#1f1a1b]">{service.label} across {city.name}</h2>
          <p className="text-[15px] text-[#5a5254] mt-2">We connect families with {service.plural.toLowerCase()} in these neighborhoods and more:</p>
          <div className="flex flex-wrap gap-2 mt-5">
            {(seo?.neighborhoods || []).map((n) => (
              <span key={n} className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3.5 py-1.5 text-[14px] text-[#3a3436]"><MapPin className="w-3.5 h-3.5 text-[#7c2233]" />{n}</span>
            ))}
          </div>

          <h3 className="font-display text-2xl text-[#1f1a1b] mt-12">Other services in {city.name}</h3>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            {otherServices.map((s) => (
              <Link key={s} to={`/${s}-${city.slug}`} className="rounded-xl border border-black/10 bg-white px-5 py-4 hover:border-[#7c2233]/30 transition-colors capitalize font-medium text-[#1f1a1b]">
                {s.replace("-", " ")} in {city.name} <ChevronRight className="w-4 h-4 inline text-[#7c2233]" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-black/[0.06]">
        <div className="max-w-3xl mx-auto px-5 py-14">
          <h2 className="font-display text-3xl sm:text-4xl text-[#1f1a1b]">FAQ: {service.label.toLowerCase()} in {city.name}</h2>
          <Accordion type="single" collapsible className="mt-8 w-full">
            {[
              { q: `How much does a ${service.label.toLowerCase()} in ${city.name} cost?`, a: `Rates for a ${service.label.toLowerCase()} in ${city.name} typically range ${rate}, depending on experience and schedule. With SimplySitter there's no placement commission — just a transparent $15.99/month membership, with the first month free.` },
              { q: `Are ${service.plural.toLowerCase()} on SimplySitter in ${city.name} vetted?`, a: `Yes. Every ${service.label.toLowerCase()} in ${city.name} verifies their identity, is personally interviewed, and passes background and reference checks before they can apply to your job.` },
              { q: `How quickly can I hire a ${service.label.toLowerCase()} in ${city.name}?`, a: `Most families in ${city.name} receive their first applications within ${stats.response} of posting a ${service.label.toLowerCase()} job. You can review profiles and message candidates right away.` },
              { q: `Can I post more than one ${service.label.toLowerCase()} job in ${city.name}?`, a: `Absolutely. Your membership lets you post multiple ${service.label.toLowerCase()} jobs across ${city.name} and other cities at no extra cost.` },
            ].map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border border-black/[0.08] rounded-2xl mb-3 px-5">
                <AccordionTrigger className="text-left font-display text-lg text-[#1f1a1b] hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[15px] text-[#5a5254] leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 pb-16">
          <div className="rounded-3xl bg-[#7c2233] px-8 py-12 text-center">
            <h2 className="font-display text-3xl text-white">Ready to find your {service.label.toLowerCase()} in {city.name}?</h2>
            <p className="text-white/80 mt-3">1 month free, then $15.99/month. Cancel anytime.</p>
            <button onClick={start} className="inline-flex mt-6 rounded-xl bg-white text-[#7c2233] font-semibold px-8 py-3.5 hover:bg-[#f7e4e6] transition-colors">Post a job free</button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
